/**
 * ReassignTasksDialog — a supervisor moves one application's open tasks to another officer on their team
 * (Bill 2026-10-16: "Supervisors Task View and the ability to reassign tasks").
 *
 * Every open task is listed and checked by default; the supervisor picks who takes them and says why. The server
 * (POST v3/follow-up-tasks/reassign) checks that both the current and the new assignee are on the caller's team,
 * requires the reason, and records each move.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ApplicationTask, ApplicationTaskGroup, TeamTaskMember } from '@retail-ops/shared-types';
import { Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { acctNumber } from './taskGroups';

interface Row {
  task: ApplicationTask;
  where: string;
}

function openRows(g: ApplicationTaskGroup): Row[] {
  const rows: Row[] = [];
  for (const a of g.accounts) {
    for (const t of a.tasks) {
      if (!t.done && t.id) rows.push({ task: t, where: [a.product, acctNumber(a.acctId)].filter(Boolean).join(' ') });
    }
  }
  for (const t of g.applicationTasks) {
    if (!t.done && t.id) rows.push({ task: t, where: g.kind === 'application' ? 'Whole application' : 'Customer' });
  }
  return rows;
}

export interface ReassignTasksDialogProps {
  group: ApplicationTaskGroup | null;
  team: ReadonlyArray<TeamTaskMember>;
  onClose: () => void;
  /** Called after a successful move with how many tasks moved. */
  onDone: (moved: number, toName: string) => void;
}

export function ReassignTasksDialog({ group, team, onClose, onDone }: ReassignTasksDialogProps): JSX.Element | null {
  const client = useApiClient();
  const rows = useMemo(() => (group ? openRows(group) : []), [group]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setChecked(new Set(rows.map((r) => r.task.id!)));
    setTo('');
    setReason('');
    setError(null);
  }, [rows]);

  if (!group) return null;

  const nameOf = (code?: string | null): string =>
    team.find((m) => m.officerCode.toUpperCase() === (code ?? '').toUpperCase())?.name ?? code ?? 'Unassigned';
  const currentHolders = new Set(rows.filter((r) => checked.has(r.task.id!)).map((r) => (r.task.assignedOfficerCode ?? '').toUpperCase()));
  const target = team.find((m) => m.officerCode === to);
  const selectedCount = checked.size;
  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= 500;
  const sameAsCurrent = !!target && currentHolders.size === 1 && currentHolders.has(target.officerCode.toUpperCase());
  const canSubmit = selectedCount > 0 && !!target && reasonOk && !sameAsCurrent && !busy;

  const toggle = (id: string): void => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setChecked(next);
  };

  const submit = async (): Promise<void> => {
    if (!canSubmit || !target) return;
    setBusy(true);
    setError(null);
    try {
      const res = await client.retail.followUpTasks.reassign({ taskIds: [...checked], toOfficerCode: target.officerCode, reason: reason.trim() });
      if (!res.success) {
        setError(res.error ?? 'The tasks could not be reassigned.');
        return;
      }
      onDone(res.moved ?? selectedCount, target.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The tasks could not be reassigned.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      open
      onClose={onClose}
      size="md"
      title={`Reassign tasks · ${group.customer}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={!canSubmit}>
            {busy ? 'Reassigning…' : `Reassign ${selectedCount} task${selectedCount === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <div className="rt-dialog">
        <p className="rt-dialog__lead">
          Choose the tasks to move and who should take them. The new officer sees them on their Home page right away,
          and the move is recorded with your reason.
        </p>

        <fieldset className="rt-dialog__tasks">
          <legend>Tasks ({selectedCount} of {rows.length} selected)</legend>
          {rows.map((r) => (
            <label key={r.task.id} className="rt-dialog__task">
              <input type="checkbox" checked={checked.has(r.task.id!)} onChange={() => toggle(r.task.id!)} />
              <span className="rt-dialog__task-text">
                <span className="rt-dialog__task-label">{r.task.label}{r.task.who ? ` · for ${r.task.who}` : ''}</span>
                <span className="rt-dialog__task-meta">{r.where} · now with {nameOf(r.task.assignedOfficerCode)}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <label className="rt-dialog__field">
          <span>Move to</span>
          <select value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">Choose an officer…</option>
            {team.map((m) => (
              <option key={m.officerCode} value={m.officerCode}>
                {m.name} ({m.officerCode}){m.branchCode ? ` · branch ${m.branchCode}` : ''} · {m.openCount} open
              </option>
            ))}
          </select>
          {sameAsCurrent && <span className="rt-dialog__hint rt-dialog__hint--warn">These tasks are already with {target?.name}.</span>}
        </label>

        <label className="rt-dialog__field">
          <span>Reason</span>
          <textarea rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="For example: Collette is out today; Crystal will finish these." />
          <span className="rt-dialog__hint">Required. Kept with each task's history.</span>
        </label>

        {error && <div className="rt-dialog__error" role="alert">{error}</div>}
      </div>
    </ModalShell>
  );
}
